/*
 * ACCOUNT TASKS BOARD
 *
 * Interactive kanban surface for the account detail Tasks tab.
 * This component renders persisted account-scoped tasks grouped by status with edit and drag/drop actions.
 *
 * Recent changes:
 * - Replaced local prototype task data with live account task loading.
 * - Preserved the account task kanban layout while adding loading and error states.
 * - Remapped visible task statuses to the Todo/In Progress/Completed/Cancel workflow labels.
 * - Restored permission-aware card dragging and double-click task editing from account/contact boards.
 * - Matches the current global Tasks board grid, lanes, compact cards, and drag surfaces.
 * - Requires a valid due date before saving an editable account or contact task.
 */

import { FC, useEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import { toast } from 'react-toastify';
import { ChatBubbleLeftRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  Task,
  TaskPriority,
  TaskStatus,
  TASK_DUE_DATE_PAST_ERROR,
  TASK_DUE_DATE_REQUIRED_ERROR,
  canMutateTask,
  getTaskDueDateValidationError,
  getTaskStatusLabel,
  getTodayDateValue,
} from '../../models/Task';
import { TASKS_UPDATED_EVENT, getTasksForAccount, updateTask, updateTaskStatus } from '../../services/taskService';
import { useTeams } from '../../context/TeamContext';
import { useEventBus } from '../../context/EventBusContext';
import { THEME_CLASSES } from '../../constants/themeConstants';
import { API_ENDPOINTS } from '../../constants/apiEndpoints';
import { EVENTS } from '../../constants/eventConstants';
import { request } from '../../services/dataService';
import Spinner from '../common/Spinner';
import { Button, Input, Textarea } from '../primitives';

interface AccountTasksPrototypeProps {
  accountId: number;
  accountName: string;
}

const LANE_ORDER: TaskStatus[] = ['Open', 'Waiting', 'Done', 'Canceled'];
const BOARD_DRAG_SCROLL_EDGE_PX = 96;
const BOARD_DRAG_SCROLL_MAX_STEP = 28;
const ACCOUNT_TASK_BOARD_HEADER_CLIP_PATHS = {
  first: 'polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%)',
  middle: 'polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%, 12px 50%)',
  last: 'polygon(0 0, 100% 0, 100% 100%, 0 100%, 12px 50%)'
};

interface UserOption {
  id: number;
  name: string;
  email?: string;
}

const PRIORITY_STYLES: Record<TaskPriority, string> = {
  High: 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-200',
  Medium: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200',
  Low: 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-200'
};

const formatDueDate = (dueDate: string | null): { label: string; tone: string } => {
  if (!dueDate) {
    return {
      label: 'No due date',
      tone: `bg-slate-100 text-slate-700 dark:bg-slate-900 dark:text-slate-300`
    };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const parsedDueDate = new Date(dueDate);
  parsedDueDate.setHours(0, 0, 0, 0);

  const diffDays = Math.round((parsedDueDate.getTime() - today.getTime()) / 86400000);

  if (diffDays < 0) {
    return {
      label: `Overdue ${parsedDueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`,
      tone: 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-200'
    };
  }

  if (diffDays === 0) {
    return {
      label: 'Due today',
      tone: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200'
    };
  }

  return {
    label: `Due ${parsedDueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`,
    tone: 'bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-200'
  };
};

const AccountTasksPrototype: FC<AccountTasksPrototypeProps> = ({ accountId, accountName }) => {
  const { currentUserId } = useTeams();
  const { publish } = useEventBus();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
  const [draggingTaskId, setDraggingTaskId] = useState<number | null>(null);
  const [hoveredLane, setHoveredLane] = useState<TaskStatus | null>(null);
  const boardScrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadTasks = async () => {
      setIsLoading(true);

      try {
        const accountTasks = await getTasksForAccount(accountId);
        if (!isMounted) {
          return;
        }

        setTasks(accountTasks);
        setError(null);
      } catch (loadError) {
        if (!isMounted) {
          return;
        }

        console.error('Failed to load account tasks:', loadError);
        setTasks([]);
        setError('Unable to load account tasks right now.');
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    void loadTasks();

    const handleTasksUpdated = () => {
      void loadTasks();
    };

    window.addEventListener(TASKS_UPDATED_EVENT, handleTasksUpdated);

    return () => {
      isMounted = false;
      window.removeEventListener(TASKS_UPDATED_EVENT, handleTasksUpdated);
    };
  }, [accountId]);

  const lanes = useMemo(
    () =>
      LANE_ORDER.map((status) => ({
        status,
        tasks: tasks.filter((task) => task.status === status)
      })),
    [tasks]
  );
  const selectedTask = useMemo(
    () => selectedTaskId === null ? null : tasks.find((task) => task.id === selectedTaskId) ?? null,
    [selectedTaskId, tasks]
  );

  const handleOpenTask = (task: Task) => {
    setSelectedTaskId(task.id);
  };

  const handleCloseTaskPanel = () => {
    setSelectedTaskId(null);
  };

  const handleTaskSaved = (savedTask: Task) => {
    setTasks((previousTasks) => previousTasks.map((task) => task.id === savedTask.id ? savedTask : task));
  };

  const handleAddFollowUpNote = (task: Task) => {
    publish(EVENTS.OPEN_NOTE_MODAL, {
      accountId: task.accountId,
      contactId: task.contactId ?? undefined,
    });
  };

  const scrollBoardDuringDrag = (event: DragEvent<HTMLElement>) => {
    const boardElement = boardScrollRef.current;

    if (!boardElement || draggingTaskId === null) {
      return;
    }

    const boardRect = boardElement.getBoundingClientRect();
    if (boardRect.height <= 0) {
      return;
    }

    const pointerY = event.clientY;
    const pointerX = event.clientX;
    const topEdge = boardRect.top + BOARD_DRAG_SCROLL_EDGE_PX;
    const bottomEdge = boardRect.bottom - BOARD_DRAG_SCROLL_EDGE_PX;
    const leftEdge = boardRect.left + BOARD_DRAG_SCROLL_EDGE_PX;
    const rightEdge = boardRect.right - BOARD_DRAG_SCROLL_EDGE_PX;
    let verticalScrollDelta = 0;
    let horizontalScrollDelta = 0;

    if (pointerY < topEdge) {
      const intensity = Math.min(1, (topEdge - pointerY) / BOARD_DRAG_SCROLL_EDGE_PX);
      verticalScrollDelta = -Math.ceil(intensity * BOARD_DRAG_SCROLL_MAX_STEP);
    } else if (pointerY > bottomEdge) {
      const intensity = Math.min(1, (pointerY - bottomEdge) / BOARD_DRAG_SCROLL_EDGE_PX);
      verticalScrollDelta = Math.ceil(intensity * BOARD_DRAG_SCROLL_MAX_STEP);
    }

    if (pointerX < leftEdge) {
      const intensity = Math.min(1, (leftEdge - pointerX) / BOARD_DRAG_SCROLL_EDGE_PX);
      horizontalScrollDelta = -Math.ceil(intensity * BOARD_DRAG_SCROLL_MAX_STEP);
    } else if (pointerX > rightEdge) {
      const intensity = Math.min(1, (pointerX - rightEdge) / BOARD_DRAG_SCROLL_EDGE_PX);
      horizontalScrollDelta = Math.ceil(intensity * BOARD_DRAG_SCROLL_MAX_STEP);
    }

    if (verticalScrollDelta !== 0) {
      boardElement.scrollTop += verticalScrollDelta;
    }

    if (horizontalScrollDelta !== 0) {
      boardElement.scrollLeft += horizontalScrollDelta;
    }
  };

  const moveTaskToStatus = async (taskId: number, targetStatus: TaskStatus) => {
    const taskToMove = tasks.find((task) => task.id === taskId);
    if (!taskToMove || taskToMove.status === targetStatus) {
      setDraggingTaskId(null);
      setHoveredLane(null);
      return;
    }

    if (!canMutateTask(taskToMove, currentUserId)) {
      setDraggingTaskId(null);
      setHoveredLane(null);
      toast.error('You can only move tasks you created or are assigned to.');
      return;
    }

    const previousTask = { ...taskToMove };
    const movedAt = new Date().toISOString();

    setTasks((previousTasks) => previousTasks.map((task) => {
      if (task.id !== taskId) {
        return task;
      }

      return {
        ...task,
        status: targetStatus,
        completedAt: targetStatus === 'Done' ? task.completedAt ?? movedAt : null,
        updatedAt: movedAt,
      };
    }));
    setDraggingTaskId(null);
    setHoveredLane(null);

    try {
      const updatedTask = await updateTaskStatus(taskId, targetStatus);
      setTasks((previousTasks) => previousTasks.map((task) => task.id === taskId ? updatedTask : task));
      toast.success(`Task moved to ${getTaskStatusLabel(targetStatus)}.`);
    } catch (moveError) {
      console.error('Failed to update account task status:', moveError);
      setTasks((previousTasks) => previousTasks.map((task) => task.id === taskId ? previousTask : task));
      toast.error('Unable to move this task right now.');
    }
  };

  const getDroppedTaskId = (event: DragEvent<HTMLElement>) => {
    if (draggingTaskId !== null) {
      return draggingTaskId;
    }

    const transferredTaskValue = event.dataTransfer.getData('text/plain');
    if (!transferredTaskValue) {
      return null;
    }

    const transferredTaskId = Number(transferredTaskValue);
    return Number.isFinite(transferredTaskId) ? transferredTaskId : null;
  };

  const handleTaskDragStart = (event: DragEvent<HTMLElement>, taskId: number) => {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(taskId));
    setDraggingTaskId(taskId);
  };

  const handleTaskDragEnd = () => {
    setDraggingTaskId(null);
    setHoveredLane(null);
  };

  const handleLaneDragOver = (event: DragEvent<HTMLElement>, lane: TaskStatus) => {
    if (draggingTaskId === null) {
      return;
    }

    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    scrollBoardDuringDrag(event);
    setHoveredLane(lane);
  };

  const handleBoardDragOver = (event: DragEvent<HTMLDivElement>) => {
    if (draggingTaskId === null) {
      return;
    }

    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    scrollBoardDuringDrag(event);
  };

  const handleLaneDragLeave = (lane: TaskStatus) => {
    setHoveredLane((currentLane) => currentLane === lane ? null : currentLane);
  };

  const handleLaneDrop = (event: DragEvent<HTMLElement>, lane: TaskStatus) => {
    event.preventDefault();

    const taskId = getDroppedTaskId(event);
    if (taskId === null) {
      setHoveredLane(null);
      return;
    }

    void moveTaskToStatus(taskId, lane);
  };

  if (isLoading) {
    return (
      <div className="flex h-full min-h-[26rem] items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full min-h-[26rem] items-center justify-center">
        <div className={`max-w-lg rounded-3xl border ${THEME_CLASSES.card.border} ${THEME_CLASSES.card.background} px-8 py-10 text-center shadow-sm`}>
          <h2 className={`text-lg font-semibold ${THEME_CLASSES.text.primary}`}>Unable to load tasks</h2>
          <p className={`mt-3 text-sm leading-6 ${THEME_CLASSES.text.secondary}`}>
            {error}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className={`grid min-h-0 flex-1 grid-cols-1 gap-4 ${selectedTask ? 'lg:grid-cols-[minmax(0,1fr)_28rem]' : ''}`}>
        <div className="flex min-h-0 min-w-0 overflow-hidden">
          <div
            ref={boardScrollRef}
            role="region"
            aria-label={`${accountName} task status board`}
            onDragOver={handleBoardDragOver}
            className={`min-h-0 flex-1 overflow-x-auto overflow-y-hidden rounded-xl border ${THEME_CLASSES.card.border} bg-white shadow-lg dark:bg-gray-800`}
          >
            <div
              data-testid="account-task-board-lanes"
              className="grid h-full min-h-0 min-w-[48rem] w-full grid-cols-4 items-stretch pb-6"
            >
              {lanes.map(({ status, tasks: laneTasks }, laneIndex) => (
                <AccountTaskKanbanLane
                  key={status}
                  lane={status}
                  tasks={laneTasks}
                  isFirst={laneIndex === 0}
                  isLast={laneIndex === LANE_ORDER.length - 1}
                  isActiveDropTarget={hoveredLane === status}
                  draggingTaskId={draggingTaskId}
                  currentUserId={currentUserId}
                  onOpenTask={handleOpenTask}
                  onDragStart={handleTaskDragStart}
                  onDragEnd={handleTaskDragEnd}
                  onDragOver={handleLaneDragOver}
                  onDragLeave={handleLaneDragLeave}
                  onDrop={handleLaneDrop}
                  onAddFollowUpNote={handleAddFollowUpNote}
                />
              ))}
            </div>
          </div>
        </div>

        {selectedTask && (
          <AccountTaskPanel
            task={selectedTask}
            currentUserId={currentUserId}
            onTaskSaved={handleTaskSaved}
            onClose={handleCloseTaskPanel}
          />
        )}
      </div>
    </div>
  );
};

const AccountTaskKanbanLane: FC<{
  lane: TaskStatus;
  tasks: Task[];
  isFirst: boolean;
  isLast: boolean;
  isActiveDropTarget: boolean;
  draggingTaskId: number | null;
  currentUserId: number | null;
  onOpenTask: (task: Task) => void;
  onDragStart: (event: DragEvent<HTMLElement>, taskId: number) => void;
  onDragEnd: () => void;
  onDragOver: (event: DragEvent<HTMLElement>, status: TaskStatus) => void;
  onDragLeave: (status: TaskStatus) => void;
  onDrop: (event: DragEvent<HTMLElement>, status: TaskStatus) => void;
  onAddFollowUpNote: (task: Task) => void;
}> = ({
  lane,
  tasks,
  isFirst,
  isLast,
  isActiveDropTarget,
  draggingTaskId,
  currentUserId,
  onOpenTask,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
  onAddFollowUpNote,
}) => {
  const headerClipPath = isFirst
    ? ACCOUNT_TASK_BOARD_HEADER_CLIP_PATHS.first
    : isLast
      ? ACCOUNT_TASK_BOARD_HEADER_CLIP_PATHS.last
      : ACCOUNT_TASK_BOARD_HEADER_CLIP_PATHS.middle;

  return (
    <section
      role="region"
      aria-label={`${getTaskStatusLabel(lane)} task lane`}
      className="flex h-full min-h-0 min-w-0 flex-col"
    >
      <div className="relative flex-shrink-0">
        <div
          className="relative flex h-11 items-center bg-green-200 px-6 text-sm font-semibold text-green-800 dark:bg-green-800 dark:text-green-50"
          style={{ clipPath: headerClipPath }}
        >
          <div className="flex w-full translate-y-1 items-baseline justify-between">
            <h2 className="min-w-0 truncate leading-5">{getTaskStatusLabel(lane)}</h2>
            <span className="ml-2 shrink-0 font-bold leading-5">{tasks.length}</span>
          </div>
        </div>
      </div>

      <div
        data-account-task-lane-dropzone={lane}
        onDragOver={(event) => onDragOver(event, lane)}
        onDragLeave={() => onDragLeave(lane)}
        onDrop={(event) => onDrop(event, lane)}
        className={`min-h-0 flex-1 space-y-4 overflow-y-auto bg-gray-50 p-4 transition-all duration-200 dark:bg-gray-900 ${isActiveDropTarget
          ? 'border-2 border-dashed border-green-500 bg-green-50 dark:bg-green-900/10'
          : 'border border-gray-200 dark:border-gray-700'
          }`}
      >
        {tasks.map((task) => (
          <TaskCard
            key={task.id}
            task={task}
            canEditTask={canMutateTask(task, currentUserId)}
            isDragging={draggingTaskId === task.id}
            onOpenTask={onOpenTask}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onAddFollowUpNote={onAddFollowUpNote}
          />
        ))}
        {tasks.length === 0 && (
          <div className={`flex h-40 items-center justify-center rounded-lg border border-dashed ${THEME_CLASSES.card.border} px-4 text-center text-sm ${THEME_CLASSES.text.secondary}`}>
            {isActiveDropTarget && draggingTaskId !== null
              ? `Drop to move this task to ${getTaskStatusLabel(lane)}.`
              : `No tasks match this account in ${getTaskStatusLabel(lane).toLowerCase()}.`}
          </div>
        )}
      </div>
    </section>
  );
};

const TaskCard: FC<{
  task: Task;
  canEditTask: boolean;
  isDragging: boolean;
  onOpenTask: (task: Task) => void;
  onDragStart: (event: DragEvent<HTMLElement>, taskId: number) => void;
  onDragEnd: () => void;
  onAddFollowUpNote: (task: Task) => void;
}> = ({ task, canEditTask, isDragging, onOpenTask, onDragStart, onDragEnd, onAddFollowUpNote }) => {
  const dueDate = formatDueDate(task.dueDate);
  const showTaskActions = canEditTask;
  const actionButtonClass = 'inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-emerald-300 disabled:cursor-not-allowed disabled:opacity-40 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-slate-50';

  const stopActionClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
  };

  return (
    <div
      draggable={showTaskActions}
      onDragStart={(event) => {
        if (!showTaskActions) {
          event.preventDefault();
          return;
        }

        onDragStart(event, task.id);
      }}
      onDragEnd={onDragEnd}
      className={`transition-all duration-150 ${isDragging ? 'opacity-50' : ''} ${showTaskActions ? 'cursor-grab active:cursor-grabbing' : ''}`}
    >
      <article
        onDoubleClick={() => onOpenTask(task)}
        className={`${THEME_CLASSES.card.background} ${THEME_CLASSES.card.hover.full} rounded-xl border ${THEME_CLASSES.card.border} p-4 shadow-lg ${showTaskActions ? '' : 'cursor-pointer'}`}
      >
        <div className="mb-3">
          <button
            type="button"
            draggable={showTaskActions}
            onClick={() => onOpenTask(task)}
            className={`block w-full text-left text-sm font-semibold leading-5 ${THEME_CLASSES.text.primary} hover:underline`}
          >
            {task.title}
          </button>
        </div>

        <dl className="mb-3 flex-1 space-y-2 text-xs">
          <div className="flex items-start justify-between gap-2">
            <dt className={`${THEME_CLASSES.text.secondary} shrink-0`}>Account:</dt>
            <dd className={`${THEME_CLASSES.text.secondary} text-right`}>{task.accountName}</dd>
          </div>
          <div className="flex items-start justify-between gap-2">
            <dt className={`${THEME_CLASSES.text.secondary} shrink-0`}>Assignee:</dt>
            <dd className={`${THEME_CLASSES.text.secondary} text-right`}>{task.assigneeName}</dd>
          </div>
          {task.contactName && (
            <div className="flex items-start justify-between gap-2">
              <dt className={`${THEME_CLASSES.text.secondary} shrink-0`}>Contact:</dt>
              <dd className={`${THEME_CLASSES.text.secondary} text-right`}>{task.contactName}</dd>
            </div>
          )}
          <div className="flex items-start justify-between gap-2">
            <dt className={`${THEME_CLASSES.text.secondary} shrink-0`}>Due:</dt>
            <dd className={`${THEME_CLASSES.text.secondary} text-right`}>{dueDate.label}</dd>
          </div>
        </dl>

        <div className={`flex items-center justify-between border-t ${THEME_CLASSES.card.border} pt-2`}>
          <div className="flex min-w-0 flex-wrap items-center gap-1">
            <span className={`rounded-full px-2 py-0.5 text-xs ${PRIORITY_STYLES[task.priority]}`}>
              {task.priority}
            </span>
            <span className="max-w-[7rem] truncate rounded-full bg-sky-100 px-2 py-0.5 text-xs text-sky-800 dark:bg-sky-950 dark:text-sky-200">
              {task.sourceLabel}
            </span>
            {!canEditTask && (
              <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                View only
              </span>
            )}
          </div>

          <div className="ml-2 flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={(event) => {
                stopActionClick(event);
                onAddFollowUpNote(task);
              }}
              className={actionButtonClass}
              title="Add follow-up note"
              aria-label="Add follow-up note"
            >
              <ChatBubbleLeftRightIcon className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      </article>
    </div>
  );
};

const AccountTaskPanel: FC<{
  task: Task | null;
  currentUserId: number | null;
  onTaskSaved: (task: Task) => void;
  onClose: () => void;
}> = ({ task, currentUserId, onTaskSaved, onClose }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<TaskStatus>('Open');
  const [priority, setPriority] = useState<TaskPriority>('Medium');
  const [dueDate, setDueDate] = useState('');
  const [users, setUsers] = useState<UserOption[]>([]);
  const [selectedAssigneeIds, setSelectedAssigneeIds] = useState<number[]>([]);
  const [isAssigneeDropdownOpen, setIsAssigneeDropdownOpen] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const assigneeDropdownRef = useRef<HTMLDivElement>(null);
  const minDueDate = useMemo(() => getTodayDateValue(), []);
  const canEditTask = task ? canMutateTask(task, currentUserId) : false;
  const assigneeOptions = useMemo(() => {
    const optionsById = new Map<number, UserOption>();
    const addOption = (option: UserOption) => {
      if (Number.isFinite(option.id) && option.id > 0 && !optionsById.has(option.id)) {
        optionsById.set(option.id, option);
      }
    };

    users.forEach(addOption);
    task?.assignees.forEach(addOption);
    task?.assigneeIds.forEach((assigneeId, index) => {
      addOption({
        id: assigneeId,
        name: task.assigneeNames[index] ?? `User #${assigneeId}`,
      });
    });
    selectedAssigneeIds.forEach((assigneeId) => {
      addOption({
        id: assigneeId,
        name: `User #${assigneeId}`,
      });
    });

    return Array.from(optionsById.values());
  }, [selectedAssigneeIds, task, users]);
  const selectedAssignees = useMemo(() => {
    const optionsById = new Map(assigneeOptions.map((assignee) => [assignee.id, assignee]));
    return selectedAssigneeIds.map((assigneeId) => optionsById.get(assigneeId) ?? {
      id: assigneeId,
      name: `User #${assigneeId}`,
    });
  }, [assigneeOptions, selectedAssigneeIds]);
  const selectedAssigneeLabel = selectedAssignees.length
    ? selectedAssignees.map((assignee) => assignee.name).join(', ')
    : 'Select assignees';

  useEffect(() => {
    if (!task) {
      return;
    }

    setTitle(task.title);
    setDescription(task.description);
    setStatus(task.status);
    setPriority(task.priority);
    setDueDate(task.dueDate ?? '');
    setSelectedAssigneeIds(task.assigneeIds);
    setIsAssigneeDropdownOpen(false);
    setError(null);
  }, [task]);

  useEffect(() => {
    if (!task || !canEditTask) {
      return;
    }

    let isMounted = true;

    const loadUsers = async () => {
      setIsLoadingUsers(true);

      try {
        const usersResponse = await request(API_ENDPOINTS.GET_USERS);
        if (!isMounted) {
          return;
        }

        setUsers((Array.isArray(usersResponse) ? usersResponse : []).map((user) => ({
          id: user.id,
          name: user.name,
          email: user.email,
        })));
      } catch (loadError) {
        if (!isMounted) {
          return;
        }

        console.error('Failed to load account task assignees:', loadError);
        setError('Unable to load assignees right now.');
      } finally {
        if (isMounted) {
          setIsLoadingUsers(false);
        }
      }
    };

    void loadUsers();

    return () => {
      isMounted = false;
    };
  }, [canEditTask, task]);

  useEffect(() => {
    if (!task || !isAssigneeDropdownOpen) {
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (!assigneeDropdownRef.current?.contains(event.target as Node)) {
        setIsAssigneeDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isAssigneeDropdownOpen, task]);

  const handleAssigneeToggle = (assigneeId: number) => {
    setSelectedAssigneeIds((previousIds) => previousIds.includes(assigneeId)
      ? previousIds.filter((selectedId) => selectedId !== assigneeId)
      : [...previousIds, assigneeId]);
  };

  const handleSave = async () => {
    if (!task || !canEditTask) {
      return;
    }

    if (!title.trim()) {
      setError('Task title is required.');
      return;
    }

    if (!selectedAssigneeIds.length) {
      setError('Select at least one assignee.');
      return;
    }

    const dueDateValidationError = getTaskDueDateValidationError(dueDate, minDueDate);
    if (dueDateValidationError) {
      setError(dueDateValidationError);
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const savedTask = await updateTask(task.id, {
        title: title.trim(),
        description: description.trim(),
        accountId: task.accountId,
        contactId: task.contactId,
        sourceNoteId: task.sourceNoteId,
        dueDate: dueDate || null,
        status,
        priority,
        assigneeIds: selectedAssigneeIds,
      });

      onTaskSaved(savedTask);
      toast.success(`Task "${savedTask.title}" updated.`);
      onClose();
    } catch (saveError) {
      console.error('Failed to update account task:', saveError);
      setError('Unable to update this task right now.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!task) {
    return null;
  }

  const panelTitle = canEditTask ? 'Edit Task' : 'View Task';

  return (
    <aside
      className={`flex min-h-0 flex-col overflow-hidden rounded-lg border ${THEME_CLASSES.card.border} ${THEME_CLASSES.card.background} shadow-sm`}
      aria-labelledby="account-task-editor-panel-title"
    >
      <div className={`flex items-start justify-between gap-3 border-b ${THEME_CLASSES.card.border} px-5 py-4`}>
        <div className="min-w-0">
          <h2 id="account-task-editor-panel-title" className={`text-base font-semibold ${THEME_CLASSES.text.primary}`}>
            {panelTitle}
          </h2>
          <p className={`mt-1 truncate text-sm ${THEME_CLASSES.text.secondary}`}>
            {task.title}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${THEME_CLASSES.text.secondary} transition-colors hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:hover:bg-slate-900 dark:hover:text-slate-50`}
          aria-label="Close task panel"
        >
          <XMarkIcon className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="space-y-5 p-6">
          <div>
            <label className={`mb-2 block text-xs font-semibold uppercase tracking-[0.16em] ${THEME_CLASSES.text.secondary}`} htmlFor="account-task-panel-title">
              Title
            </label>
            <Input
              id="account-task-panel-title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              disabled={!canEditTask}
            />
          </div>

          <div>
            <label className={`mb-2 block text-xs font-semibold uppercase tracking-[0.16em] ${THEME_CLASSES.text.secondary}`} htmlFor="account-task-panel-description">
              Description
            </label>
            <Textarea
              id="account-task-panel-description"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              rows={5}
              disabled={!canEditTask}
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className={`rounded-2xl border ${THEME_CLASSES.card.border} ${THEME_CLASSES.card.background} px-4 py-3 text-sm`}>
              <div className={`text-xs font-semibold uppercase tracking-[0.16em] ${THEME_CLASSES.text.secondary}`}>Account</div>
              <div className={`mt-2 ${THEME_CLASSES.text.primary}`}>{task.accountName}</div>
            </div>

            <div className={`rounded-2xl border ${THEME_CLASSES.card.border} ${THEME_CLASSES.card.background} px-4 py-3 text-sm`}>
              <div className={`text-xs font-semibold uppercase tracking-[0.16em] ${THEME_CLASSES.text.secondary}`}>Contact</div>
              <div className={`mt-2 ${THEME_CLASSES.text.primary}`}>{task.contactName || 'No contact'}</div>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className={`mb-2 block text-xs font-semibold uppercase tracking-[0.16em] ${THEME_CLASSES.text.secondary}`} htmlFor="account-task-panel-status">
                Status
              </label>
              <select
                id="account-task-panel-status"
                value={status}
                onChange={(event) => setStatus(event.target.value as TaskStatus)}
                className={`w-full rounded-2xl border ${THEME_CLASSES.input.border} ${THEME_CLASSES.input.background} ${THEME_CLASSES.input.text} px-3 py-2 text-sm`}
                disabled={!canEditTask}
              >
                {LANE_ORDER.map((taskStatus) => (
                  <option key={taskStatus} value={taskStatus}>
                    {getTaskStatusLabel(taskStatus)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={`mb-2 block text-xs font-semibold uppercase tracking-[0.16em] ${THEME_CLASSES.text.secondary}`} htmlFor="account-task-panel-priority">
                Priority
              </label>
              <select
                id="account-task-panel-priority"
                value={priority}
                onChange={(event) => setPriority(event.target.value as TaskPriority)}
                className={`w-full rounded-2xl border ${THEME_CLASSES.input.border} ${THEME_CLASSES.input.background} ${THEME_CLASSES.input.text} px-3 py-2 text-sm`}
                disabled={!canEditTask}
              >
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className={`mb-2 block text-xs font-semibold uppercase tracking-[0.16em] ${THEME_CLASSES.text.secondary}`} htmlFor="account-task-panel-due-date">
                Due date
              </label>
              <Input
                id="account-task-panel-due-date"
                type="date"
                min={minDueDate}
                value={dueDate}
                required
                onChange={(event) => {
                  setDueDate(event.target.value);
                  if (
                    (error === TASK_DUE_DATE_REQUIRED_ERROR || error === TASK_DUE_DATE_PAST_ERROR)
                    && !getTaskDueDateValidationError(event.target.value, minDueDate)
                  ) {
                    setError(null);
                  }
                }}
                disabled={!canEditTask}
              />
            </div>

            <div ref={assigneeDropdownRef} className={`relative rounded-2xl border ${THEME_CLASSES.input.border} ${THEME_CLASSES.input.background} px-3 py-3 text-sm`}>
              <label className={`block text-xs font-semibold uppercase tracking-[0.16em] ${THEME_CLASSES.text.secondary}`} htmlFor="account-task-panel-assignees">
                Assignee
              </label>
              <button
                id="account-task-panel-assignees"
                type="button"
                onClick={() => canEditTask && setIsAssigneeDropdownOpen((isOpen) => !isOpen)}
                className={`mt-2 flex w-full items-center justify-between gap-3 bg-transparent p-0 text-left text-sm ${THEME_CLASSES.input.text} focus:outline-none focus:ring-0 disabled:opacity-100`}
                disabled={!canEditTask || isLoadingUsers}
                aria-haspopup="true"
                aria-expanded={isAssigneeDropdownOpen}
              >
                <span className="min-w-0 truncate">{isLoadingUsers ? 'Loading assignees...' : selectedAssigneeLabel}</span>
                <span className={`shrink-0 text-xs ${THEME_CLASSES.text.secondary}`}>v</span>
              </button>

              {canEditTask && isAssigneeDropdownOpen && (
                <div className={`absolute left-0 right-0 z-20 mt-3 max-h-52 overflow-y-auto rounded-2xl border ${THEME_CLASSES.input.border} ${THEME_CLASSES.input.background} p-2 shadow-lg`} role="group" aria-label="Assignee options">
                  {assigneeOptions.map((user) => (
                    <label key={user.id} className={`flex cursor-pointer items-start gap-3 rounded-xl px-3 py-2 text-sm ${THEME_CLASSES.text.primary} hover:bg-slate-100 dark:hover:bg-slate-900`}>
                      <input
                        type="checkbox"
                        checked={selectedAssigneeIds.includes(user.id)}
                        onChange={() => handleAssigneeToggle(user.id)}
                        className="mt-0.5 rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                      />
                      <span className="min-w-0">
                        <span className="block truncate">{user.name}</span>
                        {user.email && (
                          <span className={`block truncate text-xs ${THEME_CLASSES.text.secondary}`}>{user.email}</span>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          {error && (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300">
              {error}
            </div>
          )}

          {!canEditTask && (
            <div className={`rounded-2xl border ${THEME_CLASSES.card.border} ${THEME_CLASSES.card.background} px-4 py-3 text-sm ${THEME_CLASSES.text.secondary}`}>
              You can view this task, but only the creator or an assignee can edit it.
            </div>
          )}

          <div className="flex items-center justify-end gap-3">
            <Button onClick={onClose} variant="secondary" size="sm">
              {canEditTask ? 'Cancel' : 'Close'}
            </Button>
            {canEditTask && (
              <Button onClick={handleSave} size="sm" disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save Task'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </aside>
  );
};

export default AccountTasksPrototype;
